"use client";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  MenuContainer,
  MenuBottom,
  MenuContent,
  LinkWrapper,
  Logos,
  StyledSocialLinks,
  Content,
  MenuLabel,
  BgLogo,
  Breadcrumb,
} from "./styles";
import { Body, Paragraph } from "@itchannelplus/ui/components/Typography";

import { MenuItem, MenuProps } from "./types";
import { I18nLink } from "@/i18n/routing";
import { PAGES } from "@/config/routes";
import { falsy, preventDefault, truthy } from "@itchannelplus/utils";
import { openCookieConsent } from "@itchannelplus/ui/components/CookieConsent";
import { AnimatePresence, motion } from "framer-motion";
import nl2br from "react-br";
import { useTranslations } from "next-intl";
import { decodeContact } from "@/lib/contact";

export const SimpleMenu = ({
  onClose,
  isOpened,
  menuItems,
  messages,
  ...props
}: MenuProps) => {
  const t = useTranslations();
  // Contact fields arrive encoded from the server layout; decoded for display.
  const contact = {
    phone: decodeContact(messages.phone || "") || "+41 41 914 10 60",
    whatsapp: decodeContact(messages.whatsapp || "") || "+41 79 601 58 74",
    email: decodeContact(messages.email || "") || "info[at]mondial.ch",
  };
  const [activeSubMenus, setActiveSubMenus] = useState<number[]>([]);
  useEffect(() => {
    if (typeof window != "undefined") {
      if (isOpened) window.document.body.classList.add("menu-opened");
      else window.document.body.classList.remove("menu-opened");
    }
  }, [isOpened]);
  const nextMenu = (index: number) => {
    return preventDefault(() => {
      setActiveSubMenus([...activeSubMenus, index]);
    });
  };
  const previousMenu = (index: number) => {
    return preventDefault(() => {
      //console.log("activating menus", index, [...activeSubMenus].slice(0, index));
      setActiveSubMenus([...activeSubMenus].slice(0, index));
    });
  };

  const linkClicked = () => {
    onClose && onClose();
    setActiveSubMenus([]);
  };
  //console.log("messages", messages);
  let activeMenuItems = menuItems;
  const breadcrumbLinks: MenuItem[] = [
    {
      label: "Menu",
      href: "/",
    },
  ];
  for (const v of activeSubMenus) {
    const activeMenu = activeMenuItems![v];
    breadcrumbLinks.push({
      label: activeMenu.label,
      href: activeMenu.href,
    });
    activeMenuItems = activeMenu.children;
  }
  const Address = () => {
    return (
      <Content
        transition={{ duration: 0.5, delay: 0, ease: "easeInOut" }}
        initial={{ opacity: 0, translateX: "30%" }}
        exit={{ opacity: 0, translateX: "-30%" }}
        animate={{ opacity: 1, translateX: "0" }}
        className="address-content"
      >
        <Paragraph light lineHeight={"medium"} margin={"none"}>
          <b>{messages.companyName || "Reisebüro Mondial"}</b>
          <br />
          {truthy(messages.address) && nl2br(messages.address)}
          {falsy(messages.address) && (
            <>
              Hauptstrasse 10
              <br />
              6280 Hochdorf - Luzern
            </>
          )}
        </Paragraph>
        <Paragraph light lineHeight={"medium"}>
          {t("common.phone")} : {contact.phone} <br />
          {t("common.whatsapp")} : {contact.whatsapp}{" "}
          <br />
          {t("common.email")} : {contact.email}
        </Paragraph>
        <Paragraph light lineHeight={"medium"}>
          <strong>{t("common.opening_hours")}</strong>
          <br />
          {truthy(messages.openingHours) && nl2br(messages.openingHours)}
          {falsy(messages.openingHours) && (
            <>
              Montag : 14:00 - 18:00 <br />
              Dienstag - Freitag : 10:00 - 12:00 &nbsp; 14:00 - 18:00
              <br />
              Sam - Son : Geschlossen
            </>
          )}
        </Paragraph>
      </Content>
    );
  };
  const MenuLinks = ({ items }: { items: MenuItem[] }) => {
    //console.log("MenuLinks", items);
    return (
      <Content
        transition={{ duration: 0.5, delay: 0, ease: "easeInOut" }}
        initial={{ opacity: 0, translateX: "30%" }}
        exit={{ opacity: 0, translateX: "-30%" }}
        animate={{ opacity: 1, translateX: "0" }}
        className="menu-content"
      >
        <MenuBreadCrumb items={breadcrumbLinks} />
        {items.map(({ label, href, target, children }, index) => (
          <I18nLink
            key={`menu-link-${index}`}
            href={href}
            target={target}
            onClick={children?.length ? nextMenu(index) : linkClicked}
          >
            <MenuLabel>
              {label} {children?.length ? " ›" : ""}
            </MenuLabel>
          </I18nLink>
        ))}
        {!!activeSubMenus?.length && (
          <MenuLabel small onClick={previousMenu(activeSubMenus.length - 1)}>
            ← {messages.back}
          </MenuLabel>
        )}
      </Content>
    );
  };
  const MenuBreadCrumb = ({ items }: { items: MenuItem[] }) => {
    if (items.length < 2) return <Breadcrumb></Breadcrumb>;
    return (
      <Breadcrumb>
        {items.map(({ label, href }, index) => (
          <span key={`menu-crumb-${index}`} onClick={previousMenu(index)}>
            {label} ›
          </span>
        ))}
      </Breadcrumb>
    );
  };

  return (
    <MenuContainer {...{ isOpened }} {...props}>
      <StyledSocialLinks theme="white" hideInMobile links={messages} />
      <MenuContent {...{ isOpened }}>
        {activeMenuItems && (
          <AnimatePresence>
            <Address />
            <MenuLinks
              items={activeMenuItems}
              key={'MenuLinks_' + activeMenuItems.join("_")}
            />
          </AnimatePresence>
        )}
      </MenuContent>
      <MenuBottom>
        <LinkWrapper>
          <Body>
            <I18nLink
              href={(messages.legalPageUrl || PAGES.LEGAL) as any}
              target={"_blank"}
            >
              {messages.legal}
            </I18nLink>
            &nbsp;|&nbsp;
            <a href={"#"} onClick={preventDefault(openCookieConsent)}>
              Cookies
            </a>
            &nbsp;|&nbsp;
            <a href={"https://store.iata.org/ieccacfree"} target={"_blank"}>
              {messages.iata || "IATA 81-2 0177 2"}
            </a>
            &nbsp;|&nbsp; {messages.guaranteeFund}
            <a href={"https://www.garantiefonds.ch/de/"} target={"_blank"}>
              {t("common.travel_guarantee")}
            </a>
            &nbsp;&&nbsp;
            <a href={"https://tps.travel/"} target={"_blank"}>
              TPS
            </a>
          </Body>
        </LinkWrapper>
      </MenuBottom>
      <BgLogo>{messages.companyName}</BgLogo>
    </MenuContainer>
  );
};
